import {
  AuditOutlined,
  BarChartOutlined,
  CloudDownloadOutlined,
  DashboardOutlined,
  FileSearchOutlined,
  NumberOutlined,
  PlusCircleOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  TeamOutlined,
  BankOutlined,
  ShopOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';
import type { Me, OrganizationType } from '@/types/api';

export type NavigationGroupKey = 'overview' | 'invoiceOps' | 'operations' | 'governance';

export interface NavigationItem {
  path: string;
  titleKey: string;
  icon: ReactNode;
  group: NavigationGroupKey;
  organizationTypes?: OrganizationType[];
  requiredPermission?: string;
}

export interface NavigationGroup {
  key: NavigationGroupKey;
  titleKey: string;
  defaultPath: string;
  icon: ReactNode;
}

export const navigationGroups: NavigationGroup[] = [
  { key: 'overview', titleKey: 'nav.overview', defaultPath: '/dashboard', icon: <DashboardOutlined /> },
  { key: 'invoiceOps', titleKey: 'nav.invoiceOps', defaultPath: '/invoices', icon: <FileSearchOutlined /> },
  { key: 'operations', titleKey: 'nav.operations', defaultPath: '/exceptions', icon: <BarChartOutlined /> },
  { key: 'governance', titleKey: 'nav.governance', defaultPath: '/iam', icon: <SafetyCertificateOutlined /> },
];

export const navigationItems: NavigationItem[] = [
  { path: '/dashboard', titleKey: 'nav.dashboard', icon: <DashboardOutlined />, group: 'overview' },
  { path: '/invoices', titleKey: 'nav.invoices', icon: <FileSearchOutlined />, group: 'invoiceOps' },
  { path: '/invoices/issue', titleKey: 'nav.issue', icon: <PlusCircleOutlined />, group: 'invoiceOps' },
  { path: '/customers', titleKey: 'nav.customers', icon: <TeamOutlined />, group: 'invoiceOps' },
  { path: '/numbering', titleKey: 'nav.numbering', icon: <NumberOutlined />, group: 'invoiceOps' },
  { path: '/exceptions', titleKey: 'nav.exceptions', icon: <WarningOutlined />, group: 'operations' },
  { path: '/exports', titleKey: 'nav.exports', icon: <CloudDownloadOutlined />, group: 'operations' },
  { path: '/reports', titleKey: 'nav.reports', icon: <BarChartOutlined />, group: 'operations' },
  { path: '/iam', titleKey: 'nav.iam', icon: <SafetyCertificateOutlined />, group: 'governance' },
  { path: '/enterprises', titleKey: 'nav.enterprises', icon: <BankOutlined />, group: 'governance', organizationTypes: ['system_provider'], requiredPermission: 'iam.organization.read' },
  { path: '/brands', titleKey: 'nav.brands', icon: <TeamOutlined />, group: 'governance', organizationTypes: ['enterprise'], requiredPermission: 'iam.organization.read' },
  { path: '/stores', titleKey: 'nav.stores', icon: <ShopOutlined />, group: 'governance', organizationTypes: ['enterprise', 'brand'], requiredPermission: 'iam.organization.read' },
  { path: '/audit', titleKey: 'nav.audit', icon: <AuditOutlined />, group: 'governance' },
  { path: '/settings', titleKey: 'nav.settings', icon: <SettingOutlined />, group: 'governance' },
];

export const navigationByPath = new Map(navigationItems.map((item) => [item.path, item]));

export function getNavigationItem(pathname: string): NavigationItem | undefined {
  return navigationByPath.get(pathname);
}

export function getNavigationGroup(pathname: string): NavigationGroupKey {
  return getNavigationItem(pathname)?.group ?? 'overview';
}

export function isNavigationItemAvailable(item: NavigationItem, me: Me | null) {
  if (!item.organizationTypes && !item.requiredPermission) return true;
  if (!me) return false;
  return (!item.organizationTypes || item.organizationTypes.includes(me.context.organization.organizationType))
    && (!item.requiredPermission || me.permissions.includes(item.requiredPermission));
}

export function getAvailableNavigationItems(me: Me | null) {
  return navigationItems.filter((item) => isNavigationItemAvailable(item, me));
}
